import React, { useState, useCallback, Fragment, useEffect } from 'react';
import { Row, Checkbox, Popover, Button, Form, ConfigProvider } from 'antd';
import { ReactSortable } from 'react-sortablejs';
import PropTypes from 'prop-types';
import _ from 'lodash';
import * as yup from 'yup';

import Input from 'components/Input';
import DragIcon from 'components/Icons/DragIcon';
import EditIcon from 'components/Icons/EditIcon';

import './FormView.scss';
import { DeleteIcon } from 'components/Icons';
import { RadioCheckboxControllerWrapper } from 'pages/Proposal/components/RichEditor/RichEditorModals/FormEditModal/FormView';

const FormView = ({
  form,
  columns,
  saveForm,
  handleFocus,
  setIsDraggable,
  configText,
  isRtl,
  isFromModal = false,
}) => {
  const [formFields, setFormFields] = useState(form ? form : []);
  const [selectedField, setSelectedField] = useState();
  const [updateFormTitle, setUpdateFormTitle] = useState(false);
  const [updateFields, setUpdateFields] = useState(false);

  // eslint-disable-next-line
  const debouncedSave = useCallback(
    (formFieldsCopy, columnsCopy) => {
      // _.debounce((formFieldsCopy, columnsCopy) => {
      saveForm({ formCopy: formFieldsCopy, editable: false, columns: columnsCopy });
      // }, 1000),
    },
    [saveForm]
  );

  useEffect(() => {
    setFormFields(form);
  }, [form]);

  const handleChange = (event, index) => {
    const formFieldsCopy = [...formFields];
    const columnsCopy = columns;
    switch (event.target.type) {
      case 'text':
        formFieldsCopy[index].value = event.target.value;
        break;
      case 'checkbox':
        formFieldsCopy[index]['required'] = event.target.checked;
        break;
      default:
      // do nothing
    }
    setFormFields(formFieldsCopy);
    debouncedSave(formFieldsCopy, columnsCopy);
  };

  const updateCustomField = (index, value) => {
    if (value) {
      const formFieldsCopy = [...formFields];
      formFieldsCopy[index].key = value;

      setFormFields(formFieldsCopy);
      const columnsCopy = columns;
      debouncedSave(formFieldsCopy, columnsCopy);
      setSelectedField('');
    } else {
      if (!!index) {
        setUpdateFormTitle(false);
      }
      setSelectedField(index);
    }
  };

  const updateHeadValue = (shouldOpen, value) => {
    if (value) {
      const formFieldsCopy = [...formFields];
      let addNew = true;
      for (let i = 0; i < formFieldsCopy.length; i++) {
        if (formFieldsCopy[i].head) {
          formFieldsCopy[i].key = value;
          formFieldsCopy[i].custom = true;
          addNew = false;
          break;
        }
      }
      if (addNew) {
        formFieldsCopy.push({
          key: value,
          custom: true,
          head: true,
        });
      }
      setFormFields(formFieldsCopy);
      const columnsCopy = columns;
      debouncedSave(formFieldsCopy, columnsCopy);
      setUpdateFormTitle(false);
    } else {
      if (!!shouldOpen) {
        setSelectedField(false);
      }
      setUpdateFormTitle(shouldOpen);
    }
  };

  const sortFields = (newState) => {
    if (updateFields) {
      setFormFields(newState);
      const columnsCopy = columns;
      debouncedSave(newState, columnsCopy);
      setUpdateFields(false);
    }
  };

  const removeElement = (index) => {
    const formFieldsCopy = [...formFields];
    formFieldsCopy[index] = formFieldsCopy.filter((item, i) => i !== index);
    setFormFields(formFieldsCopy);
    debouncedSave(formFieldsCopy, columns);
  };

  let formTitle = form.find((elem) => elem.head);
  if (formTitle) {
    if (formTitle.custom) {
      formTitle = formTitle.key;
    } else {
      formTitle = configText('Please fill the form below');
    }
  } else {
    formTitle = configText('Please fill the form below');
  }

  return (
    <div className="form-view">
      {!isFromModal && (
        <Row className="form-view-header">
          <h3>{formTitle}</h3>
          <Popover
            placement={isRtl ? 'topLeft' : 'topRight'}
            onVisibleChange={(visible) =>
              visible ? updateHeadValue(true) : updateHeadValue(false)
            }
            trigger="click"
            overlayClassName={`edit-container-popover ${isRtl ? 'rtl' : ''}`}
            visible={updateFormTitle}
            content={
              <div onClick={(e) => e.stopPropagation()}>
                <FieldHeadForm
                  fieldName={'Form title'}
                  onSave={(value) => updateHeadValue(true, value)}
                  onCancel={() => updateHeadValue(false)}
                  configText={configText}
                  isRtl={isRtl}
                  initialValue={formTitle}
                  handleFocus={handleFocus}
                />
              </div>
            }>
            <EditIcon
              id="form-editor-edit-button"
              className={`form-editor-edit-button`}
              onClick={(e) => e.stopPropagation()}
            />
          </Popover>
        </Row>
      )}
      <Row className="form-view-fields-row">
        <ReactSortable
          list={formFields}
          onUpdate={() => {
            setUpdateFields(true);
          }}
          setList={(newState) => {
            sortFields(newState);
          }}
          animation={400}
          handle=".handle"
          className={`form-list-items ${columns === 2 ? 'form-list-two-columns' : ''}`}>
          {_.map(formFields, (item, listIndex) =>
            item.selected || item.saved ? (
              <div
                key={isFromModal ? `from_modal_${listIndex}` : listIndex}
                className={item.selected ? 'form-list-item selected' : 'form-list-item'}>
                {['checkbox', 'radio'].includes(item.type) ? (
                  <Row className="option-controller-wrapper">
                    <div
                      className="handle-draggable handle"
                      onMouseDown={() => setIsDraggable(false)}
                      onMouseUp={() => setIsDraggable(true)}>
                      <DragIcon />
                    </div>
                    <RadioCheckboxControllerWrapper
                      wrapperStyle={{ gap: '2px' }}
                      controlStyle={{ padding: '0px', margin: '0px' }}
                      requiredStyle={{ marginTop: '-4px' }}
                      item={item}
                    />
                  </Row>
                ) : (
                  <>
                    <div className="form-label">
                      <label>{configText(item.key, true)}</label>{' '}
                      {item.required ? <span className="required">*</span> : null}
                      {item.editable ? (
                        <Popover
                          onVisibleChange={(visible) =>
                            visible ? updateCustomField(listIndex) : updateCustomField(false)
                          }
                          trigger="click"
                          overlayClassName={`edit-container-popover ${isRtl ? 'rtl' : ''}`}
                          visible={selectedField === listIndex}
                          content={
                            <div onClick={(e) => e.stopPropagation()}>
                              <FieldHeadForm
                                fieldName={'Field title'}
                                onSave={(value) => updateCustomField(listIndex, value)}
                                onCancel={() => updateCustomField(false)}
                                configText={configText}
                                isRtl={isRtl}
                                initialValue={configText(item.key, true)}
                                handleFocus={handleFocus}
                                maxLength={150}
                              />
                            </div>
                          }>
                          <EditIcon
                            id="form-editor-edit-button"
                            className={`form-editor-edit-button ${
                              item.required ? 'required-editable' : 'editable'
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Popover>
                      ) : null}
                    </div>
                    <div className="form-field-wrapper">
                      <div
                        className="handle-draggable handle"
                        onMouseDown={() => setIsDraggable(false)}
                        onMouseUp={() => setIsDraggable(true)}>
                        <DragIcon />
                      </div>
                      <Input
                        type="text"
                        placeholder={
                          item?.placeholder
                            ? item.placeholder
                            : `${configText('Enter', true)} ${configText(item.key, true)}`
                        }
                        onChange={(e) => handleChange(e, listIndex)}
                        onClick={handleFocus}
                        onFocus={handleFocus}
                        id={item.key}
                        autoFocus
                        defaultValue={item.value}
                        disabled={
                          window.location.href.indexOf('/cd/') === -1 &&
                          window.location.href.indexOf('/proposal-preview') === -1
                            ? true
                            : false
                        }
                      />
                    </div>
                  </>
                )}

                <div className="form-field-footer">
                  <Checkbox
                    name={item.key}
                    className="required-checkbox"
                    onChange={(e) => handleChange(e, listIndex)}
                    onClick={(e) => e.stopPropagation()}
                    checked={item.required}>
                    {configText('Required', true)}
                  </Checkbox>
                  {isFromModal && (
                    <div className="delete-option-wrapper" onClick={() => removeElement(listIndex)}>
                      <DeleteIcon className="remove-option-icon" />
                      <span className="delete-option-text"> {configText('Delete', true)}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Fragment key={listIndex}></Fragment>
            )
          )}
        </ReactSortable>
      </Row>
    </div>
  );
};

const FieldHeadForm = ({
  onSave,
  onCancel,
  configText,
  isRtl,
  initialValue = '',
  handleFocus,
  maxLength = 300,
  fieldName,
}) => {
  const [formInstance] = Form.useForm();

  const handleFormSubmit = async (values) => {
    onSave(values.head);
  };

  // Yup validation schema for FieldHeadForm
  const fieldHeadValidationSchema = yup.object().shape({
    head: yup
      .string()
      .required(`${fieldName} is required`)
      .max(maxLength, `${fieldName} must be less than ${maxLength} characters`)
      .trim(),
  });

  const yupAddSync = {
    async validator({ field }, value) {
      const allValues = formInstance.getFieldsValue();
      allValues[field] = value;
      await fieldHeadValidationSchema.validateAt(field, allValues, { context: allValues });
    },
  };

  return (
    <Form
      form={formInstance}
      initialValues={{ head: initialValue }}
      onFinish={handleFormSubmit}
      layout="vertical"
      className="field-head-form">
      <ConfigProvider direction={isRtl ? 'rtl' : 'ltr'}>
        <Form.Item name="head" fieldKey="head" rules={[yupAddSync]}>
          <Input
            type="text"
            placeholder={configText ? configText('Enter field head') : 'Enter field head'}
            onClick={handleFocus}
            onFocus={handleFocus}
            autoFocus
          />
        </Form.Item>
      </ConfigProvider>

      <div className="button-wrapper">
        <Button className="medium-btn secondary-btn" htmlType="submit">
          Save
        </Button>
        <Button className="medium-btn grey-btn" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};

FormView.propTypes = {
  form: PropTypes.instanceOf(Array).isRequired,
  saveForm: PropTypes.func.isRequired,
  handleFocus: PropTypes.func.isRequired,
  configText: PropTypes.func.isRequired,
};

export default FormView;
